import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Target, Users, Zap, AlertCircle } from "lucide-react";

interface Lead {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  lead_score: number | null;
  status: string | null;
}

interface LeadSelectionStepProps {
  leads: Lead[] | undefined;
  selectedLeadIds: string[];
  onToggleLead: (leadId: string) => void;
  onSelectAll: () => void;
  error?: string;
}

export function LeadSelectionStep({ 
  leads, 
  selectedLeadIds, 
  onToggleLead,
  onSelectAll,
  error 
}: LeadSelectionStepProps) {
  const hotLeads = leads?.filter(l => (l.lead_score || 0) >= 70) || [];
  const qualifiedLeads = leads?.filter(l => l.status === 'qualified') || [];

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground">
        Select the leads you want to work with in this sales cycle. AI has prioritized them by conversion probability.
      </p>
      
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Target className="h-8 w-8 mx-auto text-primary mb-2" />
            <div className="text-2xl font-bold">{hotLeads.length}</div>
            <div className="text-sm text-muted-foreground">Hot Leads</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-8 w-8 mx-auto text-cyan-500 mb-2" />
            <div className="text-2xl font-bold">{leads?.length || 0}</div>
            <div className="text-sm text-muted-foreground">Total Leads</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Zap className="h-8 w-8 mx-auto text-amber-500 mb-2" />
            <div className="text-2xl font-bold">{qualifiedLeads.length}</div>
            <div className="text-sm text-muted-foreground">Qualified</div>
          </CardContent>
        </Card>
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{selectedLeadIds.length} leads selected</span>
        <button 
          onClick={onSelectAll}
          className="text-sm text-primary hover:underline"
        >
          {selectedLeadIds.length === hotLeads.length ? 'Deselect All' : 'Select All Hot Leads'}
        </button>
      </div>
      
      <div className="space-y-2 max-h-[200px] overflow-y-auto">
        {hotLeads.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            No hot leads found. Consider harvesting more leads.
          </div>
        ) : (
          hotLeads.map(lead => (
            <div 
              key={lead.id} 
              className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted/70 cursor-pointer"
              onClick={() => onToggleLead(lead.id)}
            >
              <Checkbox 
                checked={selectedLeadIds.includes(lead.id)}
                onCheckedChange={() => onToggleLead(lead.id)}
              />
              <div className="flex-1">
                <div className="font-medium">{lead.first_name} {lead.last_name}</div>
                <div className="text-sm text-muted-foreground">{lead.company_name || lead.email}</div>
              </div>
              <Badge variant="default">Score: {lead.lead_score}</Badge>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
